import React, { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import ErrorCorrectionExercise from './ErrorCorrectionExercise';
import './ErrorCorrectionEmbed.css';
import { useExerciseSync } from '../../context/ExerciseSyncContext';

const ErrorCorrectionEmbed = ({ node, updateAttributes, editor, getPos }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const exerciseIdRef = React.useRef(node.attrs.exerciseId);
  if (!exerciseIdRef.current) exerciseIdRef.current = crypto.randomUUID();
  const exerciseId = exerciseIdRef.current;
  useEffect(() => {
    if (!node.attrs.exerciseId) updateAttributes({ exerciseId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { myState, otherState, updateState, liveUpdateCount } = useExerciseSync(exerciseId);

  // Force ProseMirror to re-measure after expand/collapse so click coordinates stay accurate
  useEffect(() => {
    if (!editor) return;
    const id = requestAnimationFrame(() => {
      try {
        editor.view.updateState(editor.view.state);
        // Mobile Safari/Chrome: re-syncing the view can leave the browser
        // thinking the (still logically-selected) editable region wants the
        // on-screen keyboard, even though the user only tapped this
        // exercise's own contentEditable=false UI. Blur ONLY if the outer
        // editor root itself still has focus (not an input inside the
        // exercise's own edit mode), so the keyboard does not pop on
        // expand/collapse.
        if (typeof document !== 'undefined' && document.activeElement === editor.view.dom) {
          editor.view.dom.blur();
        }
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror a REMOTE peer's expand/collapse live, without replaying stale
  // localStorage-hydrated data on every remount — see ExerciseSyncContext's
  // liveUpdateCount for why this can't just watch otherState.expanded alone.
  const lastLiveUpdateCountRef = React.useRef(liveUpdateCount);
  useEffect(() => {
    if (liveUpdateCount === lastLiveUpdateCountRef.current) return;
    lastLiveUpdateCountRef.current = liveUpdateCount;
    if (otherState.expanded !== undefined && otherState.expanded !== isExpanded) {
      setIsExpanded(otherState.expanded);
    }
  }, [liveUpdateCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const { sentences, title, level } = node.attrs;

  const isHomework = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-tab-type');
      if (fromRoot) return fromRoot === 'homework';
      const fromBody = typeof document !== 'undefined' ? document.body.getAttribute('data-novas-current-tab-type') : null;
      return fromBody === 'homework';
    } catch (_) {
      return false;
    }
  })();

  const isStudent = (() => {
    try {
      // Check if we're in student mode by looking for student-specific attributes
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-student-mode');
      if (fromRoot) return fromRoot === 'true';
      
      // Check if we're in a student dashboard context
      const isStudentDashboard = typeof document !== 'undefined' && 
        (window.location.pathname.includes('/student-dashboard') || 
         window.location.pathname.includes('/student-profile'));
      
      return isStudentDashboard;
    } catch (_) { return false; }
  })();

  if (!sentences || !Array.isArray(sentences)) {
    return (
      <NodeViewWrapper>
        <div className="ec-embed-error">
          <p>Error: Invalid Error Correction exercise data</p>
        </div>
      </NodeViewWrapper>
    );
  }

  // Drag handlers for visual feedback
  const handleDragStart = (e) => {
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'scale(0.95)';
    e.target.style.outline = 'none';
    e.target.style.border = 'none';
    e.target.style.boxShadow = 'none';
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    e.target.style.transform = '';
    e.target.style.outline = '';
    e.target.style.border = '';
    e.target.style.boxShadow = '';
    e.target.classList.remove('dragging');
  };

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    updateState({ expanded: next });
  };

  const handleClose = () => {
    setIsExpanded(false);
    updateState({ expanded: false });
  };

  // Allow placing cursor above/below the embed (like the other exercises)
  const insertParagraphAt = (pos) => {
    if (!editor || typeof pos !== 'number') return;
    editor
      .chain()
      .focus()
      .insertContentAt(pos, { type: 'paragraph' })
      .setTextSelection(pos + 1)
      .run();
  };

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = typeof getPos === 'function' ? getPos() : null;
    insertParagraphAt(pos);
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const base = typeof getPos === 'function' ? getPos() : null;
    if (typeof base === 'number' && node && typeof node.nodeSize === 'number') {
      insertParagraphAt(base + node.nodeSize);
    }
  };

  // Only mark the outer wrapper / preview as a drag handle when the embed
  // is collapsed. With the expanded view also flagged as data-drag-handle,
  // ProseMirror treats clicks inside it (e.g. the Close button) as
  // node-selection / drag-start gestures. Same fix as the other exercise
  // embeds.
  const dragProps = isExpanded
    ? {}
    : { draggable: true, 'data-drag-handle': '', onDragStart: handleDragStart, onDragEnd: handleDragEnd };

  return (
    <NodeViewWrapper as="div" className="ec-node-view" contentEditable={false} {...dragProps}>
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove}></div>
      <div className="ec-embed" {...dragProps}>
        {!isExpanded ? (
          <div className="ec-embed-preview" onClick={handleToggle} {...dragProps}>
            <div className="ec-embed-icon">
              <svg fill="none" height="32" viewBox="0 0 24 24" width="32" xmlns="http://www.w3.org/2000/svg">
                <path clip-rule="evenodd" d="m19 3h-14c-1.65686 0-3 1.34315-3 3v12c0 1.6569 1.34315 3 3 3h14c1.6569 0 3-1.3431 3-3v-12c0-1.65685-1.3431-3-3-3zm-15 3c0-.55228.44772-1 1-1h14c.5523 0 1 .44772 1 1v12c0 .5523-.4477 1-1 1h-14c-.55228 0-1-.4477-1-1zm1.38906 8.143h1.816v.388c0 .3975.31355.711.711.711.39389 0 .718-.31.718-.711v-.388h.059c.35125 0 .641-.2744.641-.634 0-.3555-.28554-.641-.641-.641h-.059v-2.334c0-.401-.32411-.711-.718-.711-.18711 0-.3409.0382-.48004.12074-.13304.07896-.24021.19166-.34412.31346l-2.12739 2.4703-.0007.0008c-.17777.2087-.29975.4283-.29975.7177 0 .4115.34218.697.725.697zm1.816-1.275h-.66845l.66845-.7835zm4.78814 2.416c1.451 0 2.384-1.2369 2.384-2.734v-.014c0-1.4977-.9335-2.72-2.37-2.72-1.4435 0-2.38397 1.2365-2.38397 2.734v.014c0 1.4968.91877 2.72 2.36997 2.72zm.014-1.373c-.2481 0-.4599-.1235-.6174-.3565-.1604-.2372-.2606-.5856-.2606-1.0045v-.014c0-.4195.0987-.7642.2563-.9976.1544-.2288.3625-.3494.6077-.3494.2435 0 .4557.1241.6149.3593.1616.2386.2631.5872.2631 1.0017v.014c0 .4164-.0971.7613-.254.9956-.1537.2294-.3615.3514-.61.3514zm3.3213.232h1.816v.388c0 .3975.3136.711.711.711.3939 0 .718-.31.718-.711v-.388h.059c.3513 0 .641-.2744.641-.634 0-.3555-.2855-.641-.641-.641h-.059v-2.334c0-.401-.3241-.711-.718-.711-.1871 0-.3409.0382-.48.12074-.1331.07896-.2402.19166-.3442.31346l-2.1273 2.4703-.0007.0008c-.1778.2087-.2998.4283-.2998.7177 0 .4115.3422.697.725.697zm1.816-1.275h-.6684l.6684-.7835zm-12.1445-5.868c0-.55228.44772-1 1-1s1 .44772 1 1-.44772 1-1 1-1-.44772-1-1zm4-1c-.55228 0-1 .44772-1 1s.44772 1 1 1 1-.44772 1-1-.44772-1-1-1z" fill="currentColor" fill-rule="evenodd"/>
              </svg>
            </div>
            <div className="ec-unified-preview-main">
              <div className="ec-unified-preview-title">{title || 'Error Correction Exercise'}</div>
              <div className="ec-unified-preview-meta">
                {sentences.length} sentences · Level: {level || 'B1'}
              </div>
            </div>
            <div className="ec-unified-preview-actions" onClick={e => e.stopPropagation()}>
              <button
                className="ec-unified-icon-btn"
                title="Add to homework"
                style={{ display: (isHomework || isStudent) ? 'none' : 'inline-flex' }}
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('novas:add-to-homework', {
                      detail: {
                        type: 'errorCorrection',
                        attrs: { sentences, title, level }
                      }
                    }));
                  } catch (_) {}
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h4a.5.5 0 1 0 0-1h-4a.5.5 0 0 1-.5-.5V7.207l5-5 6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293z"/>
                  <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-3.5-2a.5.5 0 0 0-.5.5v1h-1a.5.5 0 0 0 0 1h1v1a.5.5 0 1 0 1 0v-1h1a.5.5 0 1 0 0-1h-1v-1a.5.5 0 0 0-.5-.5"/>
                </svg>
              </button>
              <button
                className="ec-unified-icon-btn"
                title="Delete"
                style={{ display: isStudent ? 'none' : 'inline-flex' }}
                onClick={() => editor?.chain().focus().deleteRange({ from: getPos(), to: getPos() + node.nodeSize }).run()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0"/>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="ec-embed-expanded">
            <ErrorCorrectionExercise
              sentences={sentences}
              title={title}
              level={level}
              onClose={handleClose}
              updateAttributes={updateAttributes}
              isStudent={isStudent}
              myState={myState}
              otherState={otherState}
              updateState={updateState}
            />
          </div>
        )}
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow}></div>
    </NodeViewWrapper>
  );
};

export default ErrorCorrectionEmbed; 